import { CAMPAIGN_LEVELS } from '../../content/levels/campaign';
import { MOWER_DEFS, type MowerId } from '../../content/mowers';
import { dailyLawn, type EndlessMode } from '../../gen/modes';
import { hashString32 } from '../../sim/rng';
import { rulesHash } from '../rulesHash';
import type { CheckedRun, PostedRun } from './run';

/**
 * Where a checked run is ranked (docs/ONLINE.md, "Boards"). A board is a family (`daily`,
 * `endless:sheep`, `lawn:026:push`); a scope is the period or exact lawn within it. Every board
 * keeps each player's best only.
 */
export interface BoardEntry {
  board: string;
  scope: string;
  value: number;
  /** Which way is better: times rank lowest first, scores highest first. */
  better: 'lower' | 'higher';
}

export const DAY_MS = 24 * 60 * 60 * 1000;
/** A Daily date is current somewhere from 10:00 UTC the day before (UTC+14)... */
const EARLIEST_ZONE_MS = 14 * 60 * 60 * 1000;
/** ...until 12:00 UTC the day after (UTC-12). */
const LATEST_ZONE_MS = 12 * 60 * 60 * 1000;
/** A Daily played offline may be posted this long after its day has ended everywhere. */
export const DAILY_GRACE_MS = 2 * DAY_MS;

/** Midnight UTC at the start of a `YYYY-MM-DD` date, or null if it is not a real date. */
export function dateStartMs(date: string): number | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
  if (!m) return null;
  const [y, mo, d] = [Number(m[1]), Number(m[2]), Number(m[3])];
  const ms = Date.UTC(y, mo - 1, d);
  const back = new Date(ms);
  if (back.getUTCFullYear() !== y || back.getUTCMonth() !== mo - 1 || back.getUTCDate() !== d) {
    return null;
  }
  return ms;
}

/**
 * Whether a Daily for this local date can be posted now: it has begun somewhere on Earth, and it
 * ended everywhere no more than {@link DAILY_GRACE_MS} ago (a run played offline posts later).
 */
export function dailyOpen(date: string, nowMs: number): boolean {
  const start = dateStartMs(date);
  if (start === null) return false;
  return (
    nowMs >= start - EARLIEST_ZONE_MS && nowMs < start + DAY_MS + LATEST_ZONE_MS + DAILY_GRACE_MS
  );
}

/** The ISO week a moment falls in, as `2026-W45` (weeks start on Monday, UTC). */
export function isoWeek(nowMs: number): string {
  const day = new Date(Math.floor(nowMs / DAY_MS) * DAY_MS);
  const weekday = (day.getUTCDay() + 6) % 7; // Monday 0
  // The week belongs to the year its Thursday falls in.
  const thursday = new Date(day.getTime() + (3 - weekday) * DAY_MS);
  const year = thursday.getUTCFullYear();
  const week = Math.floor((thursday.getTime() - Date.UTC(year, 0, 1)) / (7 * DAY_MS)) + 1;
  return `${year}-W${String(week).padStart(2, '0')}`;
}

/** A campaign lawn's board: its number and the mower, since mowers clear lawns at their own pace. */
export function lawnBoard(lawn: number, mower: string): string {
  return `lawn:${String(lawn).padStart(3, '0')}:${mower}`;
}

/**
 * The scope a campaign lawn's board files a run under: `checkRun`'s key for the lawn and the stock
 * mower (so app versions with a different layout of the lawn rank apart). Null for no such lawn.
 */
export function campaignScope(lawn: number, mower: MowerId): string | null {
  const level = CAMPAIGN_LEVELS[lawn - 1];
  return level ? rulesHash({ lawn: level, mower: MOWER_DEFS[mower] }) : null;
}

/**
 * The weekly featured board (docs/ONLINE.md, "Boards"): one campaign lawn a week, any mower, the
 * fastest clean clear. The scope is the week and the lawn's own key (`CheckedRun.lawnHash`).
 */
export const FEATURED = 'featured';

/**
 * The campaign lawn featured in an ISO week (`2026-W45`): any lawn, picked by a hash of the week, so
 * the server needs no list. The server says which (GET /featured), so a game with fewer lawns than
 * the server never guesses wrong. Never a soccer lawn (owner, 2026-11-04): it plays to the whistle,
 * so every clear takes the same time and the fastest-clear board would rank nothing; its neighbor
 * stands in.
 */
export function featuredLawn(week: string, lawns = CAMPAIGN_LEVELS.length): number {
  const n = 1 + (hashString32(`featured:${week}`) % lawns);
  return isSoccerLawn(n) ? (n % lawns) + 1 : n;
}

/** Whether a campaign lawn is mower soccer (played to the whistle, so ranked by goals). */
export function isSoccerLawn(lawn: number): boolean {
  return CAMPAIGN_LEVELS[lawn - 1]?.sim.soccer !== undefined;
}

/**
 * A soccer lawn's board (owner, 2026-11-04): the most goals, then the most grass mowed, per mower.
 * Its time is the whistle's, the same for everyone, so it has no time board.
 */
export function goalsBoard(lawn: number, mower: string): string {
  return `goals:${String(lawn).padStart(3, '0')}:${mower}`;
}

/** A goals board's value: goals first, then grass mowed in tenths of a percent (0-1000). */
export function goalsValue(goals: number, mowedPermille: number): number {
  return goals * 10000 + Math.max(0, Math.min(1000, mowedPermille));
}

/** The goals and the share mowed (whole percent) in a goals board's value. */
export function goalsOf(value: number): { goals: number; percent: number } {
  return { goals: Math.floor(value / 10000), percent: Math.floor((value % 10000) / 10) };
}

/** The scope a featured week's board files a run under: the week and the lawn's key. */
export function featuredScope(week: string, lawnHash: string): string {
  return `${week}:${lawnHash}`;
}

/** A lawn's own key, without a mower: the featured board's half of the scope. */
export function lawnHash(level: unknown): string {
  return rulesHash({ lawn: level });
}

/** Monday 00:00 UTC that starts an ISO week (`2026-W45`), or null if it is not one. */
export function weekStartMs(week: string): number | null {
  const m = /^(\d{4})-W(\d{2})$/.exec(week);
  if (!m) return null;
  const year = Number(m[1]);
  const n = Number(m[2]);
  // Week 1 holds the year's first Thursday, so it holds 4 January.
  const jan4 = Date.UTC(year, 0, 4);
  const weekday = (new Date(jan4).getUTCDay() + 6) % 7;
  const start = jan4 - weekday * DAY_MS + (n - 1) * 7 * DAY_MS;
  return n >= 1 && isoWeek(start) === week ? start : null;
}

/** A community lawn's board: any mower, the fastest clear (docs/ONLINE.md). */
export function communityBoard(id: string): string {
  return `community:${id}`;
}

/**
 * A board name the server knows: `daily`, `endless:<mode>`, `lawn:<NNN>:<mower>`,
 * `goals:<NNN>:<mower>`, `community:<id>`, `featured`, and the streaks.
 */
export const BOARD_PATTERN =
  /^(daily|featured|endless:[a-z]+|(?:lawn|goals):\d{3}:[a-zA-Z]+|community:[0-9a-z]{10}|streak:current|streak:longest)$/;
/** A scope: a date and lawn key, a week, `all`, or a lawn key. */
export const SCOPE_PATTERN = /^[0-9A-Za-z:-]{1,40}$/;

/** Which way a board ranks: Endless scores, streaks and goals highest first, times lowest first. */
export function boardBetter(board: string): 'lower' | 'higher' {
  return board.startsWith('endless:') || board.startsWith('streak:') || board.startsWith('goals:')
    ? 'higher'
    : 'lower';
}

/** The Daily streak boards: days in a row now (still able to go on), and the longest ever. */
export const STREAK_CURRENT = 'streak:current';
export const STREAK_LONGEST = 'streak:longest';

/**
 * The scope of a date's Daily board, as the server files a run of it: the date and the run's lawn
 * key (`checkRun`'s hash of the lawn and the stock loaner).
 */
export function dailyScope(date: string): string {
  const daily = dailyLawn(date);
  return `${date}:${rulesHash({ lawn: daily.def, mower: MOWER_DEFS[daily.loaner] })}`;
}

export interface Streaks {
  /** Days in a row ending on the last day played. */
  current: number;
  longest: number;
  /** The last day the Daily was cleared. */
  last: string;
}

/** The streaks in a set of days the Daily was cleared (any order, repeats ignored). */
export function streaksFrom(dates: readonly string[]): Streaks | null {
  const days = [...new Set(dates)]
    .map((d) => ({ d, ms: dateStartMs(d) }))
    .filter((x): x is { d: string; ms: number } => x.ms !== null)
    .sort((a, b) => a.ms - b.ms);
  const last = days.at(-1);
  if (!last) return null;
  let run = 1;
  let longest = 1;
  for (let i = 1; i < days.length; i++) {
    run = days[i]!.ms - days[i - 1]!.ms === DAY_MS ? run + 1 : 1;
    longest = Math.max(longest, run);
  }
  return { current: run, longest, last: last.d };
}

/**
 * The earliest last day a current streak can have and still go on: the day after it must not have
 * ended everywhere yet (it ends last in UTC-12, at 12:00 UTC the day after it).
 */
export function streakCutoffDate(nowMs: number): string {
  const cutoff = nowMs - 2 * DAY_MS - LATEST_ZONE_MS;
  const day = (Math.floor(cutoff / DAY_MS) + 1) * DAY_MS;
  return new Date(day).toISOString().slice(0, 10);
}

export function endlessBoard(mode: EndlessMode): string {
  return `endless:${mode}`;
}

/**
 * The boards a checked run goes on. Times count only when the lawn was cleared without a paid
 * Continue; an Endless run counts its score this week and of all time. `nowMs` is when the server
 * received it.
 */
export function boardEntries(run: PostedRun, checked: CheckedRun, nowMs: number): BoardEntry[] {
  const { board } = run;
  const clean = checked.status === 'won' && !checked.revived;
  switch (board.kind) {
    case 'daily':
      return clean && dailyOpen(board.date, nowMs)
        ? [
            {
              board: 'daily',
              scope: `${board.date}:${checked.lawnKey}`,
              value: checked.ticks,
              better: 'lower',
            },
          ]
        : [];
    case 'campaign': {
      if (!clean) return [];
      // Soccer plays to the whistle: ranked by goals, then grass mowed (not featured).
      if (checked.soccer) {
        return [
          {
            board: goalsBoard(board.lawn, run.mower),
            scope: checked.lawnKey,
            value: goalsValue(checked.soccer.goals, checked.soccer.mowedPermille),
            better: 'higher',
          },
        ];
      }
      const week = isoWeek(nowMs);
      return [
        {
          board: lawnBoard(board.lawn, run.mower),
          scope: checked.lawnKey,
          value: checked.ticks,
          better: 'lower',
        },
        // This week's featured lawn: on its board too, whatever the mower.
        ...(board.lawn === featuredLawn(week)
          ? [
              {
                board: FEATURED,
                scope: featuredScope(week, checked.lawnHash),
                value: checked.ticks,
                better: 'lower' as const,
              },
            ]
          : []),
      ];
    }
    case 'endless':
      return checked.score > 0
        ? [`week:${isoWeek(nowMs)}`, 'all'].map((scope) => ({
            board: endlessBoard(board.mode),
            scope,
            value: checked.score,
            better: 'higher' as const,
          }))
        : [];
    case 'race':
      // Races are judged in their room, not ranked (docs/LIVE.md).
      return [];
    case 'community':
      return clean
        ? [{ board: communityBoard(board.id), scope: 'all', value: checked.ticks, better: 'lower' }]
        : [];
  }
}

/**
 * A player's rank as a percentile of everyone on the board ("top 12%"): the share of entries that
 * are better, rounded up so the leader is "top 1%", never 0.
 */
export function topPercent(rank: number, total: number): number {
  if (total <= 0) return 100;
  return Math.max(1, Math.min(100, Math.ceil((rank / total) * 100)));
}
